import React, { useState, useMemo } from "react";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import { ColumnHashProto } from "build-ts/data";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Stack,
  Text,
  Badge,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select
} from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import { AnalysisColumnType, AggregationType, ColumnType, MetadataTypeEnum } from "build-ts/enums";
import { GrowthBenchmarkProto, GrowthBenchmarkType, DatePeriodEnum } from "build-ts/relative_date";
import { ObservationColumnProto } from "build-ts/metadata";
import { KGStepInterface } from "./Interface";
import { getKGGrowthBenchmarks } from "utils/analysisUtils";

interface KGBenchmarkConfigProps {
  currentNode: KGStepInterface;
  selectedBenchmarks: BenchmarkEntityProto[];
  setSelectedBenchmarks: React.Dispatch<React.SetStateAction<BenchmarkEntityProto[]>>;
  // New props for analysis_columns
  analysisColumns: ColumnHashProto[];
  setAnalysisColumns: React.Dispatch<React.SetStateAction<ColumnHashProto[]>>;
  selectedColumns: ObservationColumnProto[];
}

interface BenchmarkSelection {
  benchmark: BenchmarkEntityProto;
  columnTypes: AnalysisColumnType[];
}

const KGBenchmarkConfig = ({ 
  currentNode,
  selectedBenchmarks, 
  setSelectedBenchmarks, 
  analysisColumns,
  setAnalysisColumns,
  selectedColumns = []
}: KGBenchmarkConfigProps) => {
  // State for the current benchmark selection
  const [currentSelection, setCurrentSelection] = useState<BenchmarkSelection | null>(null);
  
  // State for Last N Period Aggregation
  const [numberOfPeriods, setNumberOfPeriods] = useState<number>(4);
  
  // State for Generalized Growth Benchmark
  const [shiftPeriod, setShiftPeriod] = useState<DatePeriodEnum>(DatePeriodEnum.MONTH);
  const [generalizedNumberOfPeriods, setGeneralizedNumberOfPeriods] = useState<number>(1);
  const [generalizedAggregationType, setGeneralizedAggregationType] = useState<AggregationType>(AggregationType.AVERAGE);
  
  // Column type options
  const columnTypeOptions = [
    { value: AnalysisColumnType.COMPOSITION, label: "Composition" },
    { value: AnalysisColumnType.VARIANCE, label: "Variance" },
    { value: AnalysisColumnType.VARIANCE_PERCENTAGE, label: "Variance Percentage" },
    { value: AnalysisColumnType.VARIANCE_CONTRIBUTION, label: "Variance Contribution" },
    { value: AnalysisColumnType.NORMALISED_TARGET, label: "Normalised Target" },
    { value: AnalysisColumnType.ACCOMPLISH_PERCENTAGE, label: "Accomplish Percentage" },
    { value: AnalysisColumnType.COMPOSITION_PERCENTAGE, label: "Composition Percentage" },
  ];

  // Check if current selection is a Last N Period Aggregation benchmark
  const isLastNPeriodAggregation = useMemo(() => {
    if (!currentSelection?.benchmark?.growth_benchmark) return false;
    return (
      currentSelection.benchmark.growth_benchmark.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      currentSelection.benchmark.growth_benchmark?.number && 
      currentSelection.benchmark.growth_benchmark?.number > 1 &&
      currentSelection.benchmark.growth_benchmark?.aggregation_type &&
      currentSelection.benchmark.growth_benchmark?.aggregation_type === AggregationType.AVERAGE
    );
  }, [currentSelection]);

  // Check if current selection is a simple Last X benchmark (LAST_DAY, LAST_WEEK, etc.)
  const isSimpleLastBenchmark = useMemo(() => {
    if (!currentSelection?.benchmark?.growth_benchmark) return false;
    return (
      currentSelection.benchmark.growth_benchmark.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      currentSelection.benchmark.growth_benchmark?.number && 
      currentSelection.benchmark.growth_benchmark?.number === 1
    );
  }, [currentSelection]);

  // Check if current selection is a Generalized Growth Benchmark (old style - should be hidden now)
  const isGeneralizedGrowthBenchmark = useMemo(() => {
    if (!currentSelection?.benchmark?.growth_benchmark) return false;
    return (
      currentSelection.benchmark.growth_benchmark.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      currentSelection.benchmark.growth_benchmark?.number && 
      currentSelection.benchmark.growth_benchmark?.number > 1 &&
      currentSelection.benchmark.growth_benchmark?.aggregation_type &&
      currentSelection.benchmark.growth_benchmark?.aggregation_type !== AggregationType.AVERAGE
    );
  }, [currentSelection]);

  // Check if there are any date filters in the current node
  const hasDateFilters = useMemo(() => {
    if (!currentNode?.selected_filters) return false;
    return currentNode.selected_filters.some(filter => 
      filter.date_filter !== undefined && filter.date_filter !== null
    );
  }, [currentNode?.selected_filters]);

  // Filter benchmarks to only show growth benchmarks when date filters are present
  const availableBenchmarks = useMemo(() => {
    if (!currentNode?.benchmarks) return [];
    
    // Get regular benchmarks (non-growth)
    const regularBenchmarks = currentNode.benchmarks.filter(benchmark => 
      !benchmark.growth_benchmark || 
      benchmark.growth_benchmark.growth_type === undefined
    );
    
    if (!hasDateFilters) {
      // If no date filters, only show regular benchmarks
      return regularBenchmarks;
    }
    
    // If date filters are present, generate dynamic growth benchmarks
    const dynamicGrowthBenchmarks = getKGGrowthBenchmarks(currentNode.selected_filters);
    
    // Combine regular benchmarks with dynamically generated growth benchmarks
    return [...regularBenchmarks, ...dynamicGrowthBenchmarks];
  }, [currentNode?.benchmarks, currentNode?.selected_filters, hasDateFilters]);

  // Handle benchmark selection
  const handleBenchmarkSelect = (benchmark: BenchmarkEntityProto) => {
    // Check if benchmark is already selected
    const isAlreadySelected = selectedBenchmarks.some(
      (selected) => selected.header?.id === benchmark.header?.id
    );

    if (isAlreadySelected) {
      // Show the column type selection but don't add it again
      setCurrentSelection({
        benchmark,
        columnTypes: [],
      });
    } else {
      // Set as current selection for column type selection
      setCurrentSelection({
        benchmark,
        columnTypes: [],
      });
    }
    
    // If it's a Last N Period Aggregation, set the default values
    const benchmarkName = benchmark.header?.name || "";
    if (
      benchmark.growth_benchmark?.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName.includes("Last N") && benchmarkName.includes("Average")
    ) {
      if (benchmark.growth_benchmark.number) {
        setNumberOfPeriods(benchmark.growth_benchmark.number);
      }
      // Aggregation type is fixed to AVERAGE for Last N benchmarks
    }

    // If it's a simple Last X benchmark, no configuration needed (fixed values)
    if (
      benchmark.growth_benchmark?.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName.startsWith("Last ") && !benchmarkName.includes("Last N") && !benchmarkName.includes("Average")
    ) {
      // No configuration needed - these have fixed values
    }

    // If it's a Generalized Growth Benchmark (old style), set the default values
    if (
      benchmark.growth_benchmark?.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName === "Generalized Growth Benchmark"
    ) {
      if (benchmark.growth_benchmark.number) {
        setGeneralizedNumberOfPeriods(benchmark.growth_benchmark.number);
      }
      if (benchmark.growth_benchmark.aggregation_type) {
        setGeneralizedAggregationType(benchmark.growth_benchmark.aggregation_type);
      }
      if (benchmark.growth_benchmark.growth_period) {
        setShiftPeriod(benchmark.growth_benchmark.growth_period);
      }
    }
  };

  // Toggle a specific column type (add or remove)
  const toggleColumnType = (columnType: AnalysisColumnType) => {
    if (!currentSelection) return;
    
    // Check if this column type is already selected
    if (currentSelection.columnTypes.includes(columnType)) {
      // Remove it
      setCurrentSelection({
        ...currentSelection,
        columnTypes: currentSelection.columnTypes.filter(type => type !== columnType),
      });
    } else {
      // Add it
      setCurrentSelection({
        ...currentSelection,
        columnTypes: [...currentSelection.columnTypes, columnType],
      });
    }
  };

  // Add current selection to the list
  const handleAddSelection = () => {
    if (!currentSelection) return;

    // Check if benchmark is already in the selection
    const benchmarkExists = selectedBenchmarks.some(
      (b) => b.header?.id === currentSelection.benchmark.header?.id
    );

    // Create a copy of the benchmark to modify if needed
    let benchmarkToAdd = { ...currentSelection.benchmark };
    
    // If it's a Last N Period Aggregation, update its configuration
    if (isLastNPeriodAggregation && benchmarkToAdd.growth_benchmark) {
      const shiftPeriod = benchmarkToAdd.growth_benchmark.growth_period;
      let shiftPeriodName = "PERIOD";
      
      // Convert enum to proper string name
      switch (shiftPeriod) {
        case DatePeriodEnum.DAY:
          shiftPeriodName = "DAY";
          break;
        case DatePeriodEnum.WEEK:
          shiftPeriodName = "WEEK";
          break;
        case DatePeriodEnum.MONTH:
          shiftPeriodName = "MONTH";
          break;
        case DatePeriodEnum.QUARTER:
          shiftPeriodName = "QUARTER";
          break;
        case DatePeriodEnum.YEAR:
          shiftPeriodName = "YEAR";
          break;
      }
      
      const customName = `LAST ${numberOfPeriods} ${shiftPeriodName} AVERAGE`;
      
      benchmarkToAdd = {
        ...benchmarkToAdd,
        header: {
          ...benchmarkToAdd.header,
          name: customName
        },
        growth_benchmark: GrowthBenchmarkProto.create({
          growth_type: GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK,
          number: numberOfPeriods,
          aggregation_type: AggregationType.AVERAGE, // Fixed to AVERAGE
          growth_period: shiftPeriod,
        })
      };
    }

    // If it's a simple Last X benchmark, no modification needed
    if (isSimpleLastBenchmark) {
      // No configuration needed - use as-is with fixed values
    }

    // If it's a Generalized Growth Benchmark (old style), update its configuration
    if (isGeneralizedGrowthBenchmark && benchmarkToAdd.growth_benchmark) {
      // Create a custom name based on selected values
      let aggregationTypeName = "Average";
      switch (generalizedAggregationType) {
        case AggregationType.SUM: aggregationTypeName = "Sum"; break;
        case AggregationType.MIN: aggregationTypeName = "Min"; break;
        case AggregationType.MAX: aggregationTypeName = "Max"; break;
        case AggregationType.COUNT: aggregationTypeName = "Count"; break;
        case AggregationType.UNIQUE_COUNT: aggregationTypeName = "Unique Count"; break;
        default: aggregationTypeName = "Average";
      }
      
      const shiftPeriodName = shiftPeriod.toString().charAt(0) + shiftPeriod.toString().slice(1).toLowerCase();
      const customName = `${generalizedNumberOfPeriods} ${shiftPeriodName}(s) ago ${aggregationTypeName}`;
      
      benchmarkToAdd = {
        ...benchmarkToAdd,
        header: {
          ...benchmarkToAdd.header,
          name: customName
        },
        growth_benchmark: GrowthBenchmarkProto.create({
          growth_type: GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK,
          number: generalizedNumberOfPeriods,
          aggregation_type: generalizedAggregationType,
          growth_period: shiftPeriod,
        })
      };
    }

    // Update selectedBenchmarks if not already included
    const updatedBenchmarks = benchmarkExists 
      ? selectedBenchmarks.map(b => 
          b.header?.id === benchmarkToAdd.header?.id ? benchmarkToAdd : b
        )
      : [...selectedBenchmarks, benchmarkToAdd];

    // NEW APPROACH: Create ColumnHashProto entries for each column, benchmark, and type
    const newAnalysisColumns: ColumnHashProto[] = [];

    // Filter to measure columns that have IDs
    const measureColumns: ObservationColumnProto[] = [];
    measureColumns.push(...selectedColumns.filter(col => col.kg_column_header?.column_type === ColumnType.MEASURE || col.kg_column_header?.type === MetadataTypeEnum.METRIC));

    // Use the selected column types for all benchmarks
    const columnTypesToUse = currentSelection.columnTypes;

    // For each column and selected column type, create a ColumnHashProto
    measureColumns?.forEach(column => {
      columnTypesToUse.forEach(columnType => {
        if (benchmarkToAdd.header?.id) {
          newAnalysisColumns.push({
            actuals_id: column.column_id,
            benchmark_id: benchmarkToAdd.header.id,
            analysis_column_type: columnType,
            is_material: false
          });
        }
      });
    });

    // For each metric also create a column hash for each selected column type.
    currentNode?.metrics?.forEach(metric => {
      columnTypesToUse.forEach(columnType => {
        if (metric.header?.id && benchmarkToAdd?.header?.id) {
          newAnalysisColumns.push({
            actuals_id: metric?.header?.id,
            benchmark_id: benchmarkToAdd.header?.id,
            analysis_column_type: columnType,
            is_material: false
          });
        }
      });
    });
    // Combine with existing analysis columns, removing any duplicates
    const updatedAnalysisColumns = [...analysisColumns];
    newAnalysisColumns?.forEach(newColumn => {
      // Check if this exact combination already exists
      const exists = updatedAnalysisColumns.some(col => 
        col.actuals_id === newColumn.actuals_id && 
        col.benchmark_id === newColumn.benchmark_id && 
        col.analysis_column_type === newColumn.analysis_column_type
      );
      
      if (!exists) {
        updatedAnalysisColumns.push(newColumn);
      }
    });

    // Update state
    setSelectedBenchmarks(updatedBenchmarks);
    setAnalysisColumns(updatedAnalysisColumns);
    
    // Reset current selection
    setCurrentSelection(null);
  };

  // Remove a benchmark and its calculations
  const handleRemoveBenchmark = (benchmarkId: string) => {
    // Remove from selectedBenchmarks
    const updatedBenchmarks = selectedBenchmarks.filter((b) => b.header?.id !== benchmarkId);
    
    // Remove from analysisColumns (New approach)
    const updatedAnalysisColumns = analysisColumns.filter(
      col => col.benchmark_id !== benchmarkId
    );
    
    // Update state
    setSelectedBenchmarks(updatedBenchmarks);
    setAnalysisColumns(updatedAnalysisColumns);
  };

  // Helper function to get benchmark name
  const getBenchmarkName = (benchmark: BenchmarkEntityProto | null): string => {
    return benchmark?.header?.name || "Unnamed Benchmark";
  };

  // Get column type label from value
  const getColumnTypeLabel = (type: AnalysisColumnType): string => {
    // Ensure we're handling undefined or null
    if (type === undefined || type === null) {
      return "Unknown";
    }
    
    // Map enum values to display labels
    switch (type) {
      case AnalysisColumnType.COMPOSITION:
        return "Composition";
      case AnalysisColumnType.VARIANCE:
        return "Variance";
      case AnalysisColumnType.VARIANCE_PERCENTAGE:
        return "Variance Percentage";
      case AnalysisColumnType.VARIANCE_CONTRIBUTION:
        return "Variance Contribution";
      case AnalysisColumnType.NORMALISED_TARGET:
        return "Normalised Target";
      case AnalysisColumnType.ACCOMPLISH_PERCENTAGE:
        return "Accomplish Percentage";
      case AnalysisColumnType.COMPOSITION_PERCENTAGE:
        return "Composition Percentage";
      case AnalysisColumnType.NOT_ANALYSIS_COLUMN:
        return "Not Analysis Column";
      default:
        return `Type ${type}`;
    }
  };

  // Get column types for a benchmark
  const getColumnTypesForBenchmark = (benchmarkId: string): AnalysisColumnType[] => {
    const types: AnalysisColumnType[] = [];
    analysisColumns.forEach(col => {
      if (col.benchmark_id === benchmarkId && col.analysis_column_type) {
        if (!types.includes(col.analysis_column_type)) {
          types.push(col.analysis_column_type);
        }
      }
    });
    return types;
  };
  
  return (
    <Stack spacing={4}>
      {/* Benchmark Selection Dropdown */}
      <FormControl>
        <FormLabel>Select Benchmark</FormLabel>
        <Menu closeOnSelect={true}>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            w="100%"
            textAlign="left"
            variant="outline"
            colorScheme="blue"
            isDisabled={availableBenchmarks.length === 0}
          >
            {currentSelection
              ? getBenchmarkName(currentSelection.benchmark)
              : availableBenchmarks.length === 0
              ? "No benchmarks available"
              : "Select a benchmark"}
          </MenuButton>
          <Portal>
            <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
              {availableBenchmarks.map((benchmark, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleBenchmarkSelect(benchmark)}
                  _hover={{ bg: "blue.50" }}
                >
                  <Flex alignItems="center" width="100%">
                    {getBenchmarkName(benchmark)}
                  </Flex>
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </FormControl>

      {/* Last N Period Aggregation Configuration */}
      {isLastNPeriodAggregation && (
        <FormControl>
          <FormLabel>Last N Period Configuration</FormLabel>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
            <Box>
              <Text fontSize="xs" mb={1}>Number of Periods (N)</Text>
              <NumberInput 
                min={1} 
                value={numberOfPeriods} 
                onChange={(_, value) => setNumberOfPeriods(value)}
                size="sm"
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </Box>
          </Box>
        </FormControl>
      )}

      {/* Generalized Growth Benchmark Configuration */}
      {isGeneralizedGrowthBenchmark && (
        <FormControl>
          <FormLabel>Generalized Growth Benchmark Configuration</FormLabel>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
            <Stack spacing={3}>
              <HStack spacing={4}>
                <Box>
                  <Text fontSize="xs" mb={1}>Number of Periods</Text>
                  <NumberInput 
                    min={1} 
                    value={generalizedNumberOfPeriods} 
                    onChange={(_, value) => setGeneralizedNumberOfPeriods(value)}
                    size="sm"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </Box>
                <Box>
                  <Text fontSize="xs" mb={1}>Aggregation Type</Text>
                  <Select 
                    size="sm" 
                    value={generalizedAggregationType}
                    onChange={(e) => setGeneralizedAggregationType(parseInt(e.target.value, 10) as AggregationType)}
                  >
                    <option value={AggregationType.SUM}>Sum</option>
                    <option value={AggregationType.AVERAGE}>Average</option>
                    <option value={AggregationType.COUNT}>Count</option>
                    <option value={AggregationType.UNIQUE_COUNT}>Unique Count</option>
                    <option value={AggregationType.MIN}>Min</option>
                    <option value={AggregationType.MAX}>Max</option>
                  </Select>
                </Box>
              </HStack>
              <HStack spacing={4}>
                <Box>
                  <Text fontSize="xs" mb={1}>Shift Period</Text>
                  <Select 
                    size="sm" 
                    value={shiftPeriod}
                    onChange={(e) => setShiftPeriod(parseInt(e.target.value, 10) as DatePeriodEnum)}
                  >
                    <option value={DatePeriodEnum.DAY}>Day</option>
                    <option value={DatePeriodEnum.WEEK}>Week</option>
                    <option value={DatePeriodEnum.MONTH}>Month</option>
                    <option value={DatePeriodEnum.QUARTER}>Quarter</option>
                    <option value={DatePeriodEnum.YEAR}>Year</option>
                  </Select>
                </Box>
              </HStack>
            </Stack>
          </Box>
        </FormControl>
      )}

      {/* Column Type Selection - visible when a benchmark is selected */}
      {currentSelection && (
        <FormControl>
          <FormLabel>Select Column Type(s)</FormLabel>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
            <Menu closeOnSelect={false}>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                w="100%"
                textAlign="left"
                variant="outline"
                colorScheme="blue"
              >
                {currentSelection.columnTypes.length > 0
                  ? `Selected Column Types (${currentSelection.columnTypes.length})`
                  : "Select column types"}
              </MenuButton>
              <Portal>
                <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
                  {columnTypeOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      onClick={() => toggleColumnType(option.value)}
                      closeOnSelect={false}
                      _hover={{ bg: "blue.50" }}
                    >
                      <Checkbox
                        isChecked={currentSelection.columnTypes.includes(option.value)}
                        pointerEvents="none"
                        mr={2}
                        colorScheme="blue"
                        mb={0}
                      />
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
            
            <Button
              mt={3}
              colorScheme="blue"
              onClick={handleAddSelection}
              width="100%"
            >
              Add Selection
            </Button>
          </Box>
        </FormControl>
      )}

      {/* Display selected benchmarks and their calculations */}
      {selectedBenchmarks.length > 0 && (
        <Box mt={0}>
          <Text fontWeight="medium" mb={2}>
            Selected Benchmarks:
          </Text>
          <Stack spacing={2}>
            {selectedBenchmarks?.map((benchmark) => {
              // Get column types using the new approach
              const columnTypes = getColumnTypesForBenchmark(benchmark.header?.id || "");
              
              return (
                <Box
                  key={benchmark.header?.id}
                  borderWidth={1}
                  borderRadius="md"
                  p={1}
                  backgroundColor="gray.50"
                >
                  <Flex justifyContent="space-between" alignItems="center" mb={2}>
                    <Text fontWeight="bold" mb={0}>
                      {getBenchmarkName(benchmark)}
                    </Text>
                    <IconButton
                      aria-label="Remove benchmark"
                      icon={<CloseIcon />}
                      size="xs"
                      onClick={() => handleRemoveBenchmark(benchmark.header?.id || "")}
                      variant="ghost"
                      colorScheme="red"
                    />
                  </Flex>
                  
                  {/* Display selected column types using the new approach */}
                  <Flex flexWrap="wrap" gap={1}>
                    {columnTypes.map((type, index) => (
                      <Badge key={index} colorScheme="blue">
                        {getColumnTypeLabel(type)}
                      </Badge>
                    ))}
                  </Flex>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default KGBenchmarkConfig; 